/* lib/push.js -- EL PUSH DE iOS EN LA APP (#464). UNA sola copia.
 *
 * Lo que hace, y nada mas (diseno de notificaciones, secciones 4 a 7):
 *
 *   . `arrancarPush()`: en el arranque con sesion y tras cada inicio de sesion. Pone los
 *     escuchas del plugin y, si el permiso YA esta concedido, pide el token (`register`).
 *     El token llega por el escucha `registration` y viaja a `POST /api/push/register`
 *     junto con el idioma de la app; se guarda en memoria y en `localStorage`.
 *   . `pedirPermisoSiToca()`: al empezar a procesar un documento (las cinco puertas de
 *     subida). Solo si el permiso esta en `prompt` muestra el dialogo del sistema, y si
 *     dicen que si, registra. Cualquier otro estado: nada. iOS lo muestra una sola vez.
 *   . `reRegistrarIdioma(lang, user)`: al cambiar el idioma, el token vuelve a viajar
 *     con el idioma nuevo (el aviso sale en el idioma del telefono).
 *   . `darDeBajaPush()`: al cerrar sesion, ANTES de borrar la sesion (la baja la pide la
 *     misma sesion). Si falla, se sigue.
 *   . El toque: `abrirDocumento(doc_id)` lleva al Panel y pone la tarjeta a la vista
 *     (`[data-testid="doc-<id>"]`), reintentando hasta 3 s mientras el Panel carga. No
 *     abre Estudiar (regla #412). Quien presta el `navigate` del router es
 *     `components/PushPuente.js`; si el toque llega antes (app matada), se guarda.
 *     series-7 (#490): una PARTE vive dentro de su tarjeta padre, que nace plegada, y
 *     plegada su marca no existe. Antes de cada vistazo se avisa con el evento de ventana
 *     `EVENTO_MOSTRAR_DOC` (`detail.id`), y la padre de esa parte se abre sola
 *     (`components/TarjetaPadreDePartes.js`). Con dos marcas iguales (las dos gemelas de
 *     la padre abierta) se pone a la vista la que se ve.
 *
 * LOS TRES CINTURONES, los de `lib/splashNativo.js` y `lib/haptics.js`:
 *
 *   1. GATE app-only y shell nativo real, evaluado POR LLAMADA. En web ninguna funcion
 *      de aqui hace nada, y el plugin JAMAS se importa.
 *   2. IMPORT DINAMICO Y CACHEADO del modulo, y la promesa guarda el MODULO, nunca el
 *      plugin pelado: `PushNotifications` es un proxy de `registerPlugin` y resolver una
 *      promesa con el colgaria la promesa para siempre (el hang de boot de F4.2c).
 *   3. A PRUEBA DE TODO: ninguna falla del plugin, del permiso o de la red llega al
 *      usuario. Cero textos nuevos en pantalla.
 *
 * SIN `import api` ARRIBA, a proposito: `lib/language.js` (chasis) importa este modulo,
 * y un `api` en el cuerpo arrastraria `src/i18n` a las suites del menu (leccion 28b). El
 * cliente y el idioma se piden al llamar.
 */
import { IS_APP_BUILD } from './buildTarget';

/** Donde vive el token entre arranques. */
export const LLAVE_TOKEN = 'sb_push_token';
/** Cuanto se busca la tarjeta tras el toque, y cada cuanto. */
export const BUSCAR_TARJETA_MS = 3000;
export const PASO_MS = 100;
/** La baja al cerrar sesion no puede atorar el cierre: techo propio. */
export const BAJA_TECHO_MS = 3000;
/** series-7 (#490): "pon a la vista este documento". Lo escucha la tarjeta padre. */
export const EVENTO_MOSTRAR_DOC = 'studybuddy:mostrar-doc';

let moduloPromise = null;
let tokenEnMemoria = null;
let escuchasPuestos = false;
let navegador = null;
let pendiente = null;

function cliente() { return require('./api').default; }

/** es / en, igual que `normalizeLanguage` de `lib/language.js` (sin importarlo: ciclo). */
export function idiomaDeLaApp(lng) {
  let valor = lng;
  if (valor === undefined) {
    try { valor = require('../i18n').default.language; } catch (e) { valor = ''; }
  }
  return String(valor || '').toLowerCase().startsWith('es') ? 'es' : 'en';
}

/** True solo en el build de app corriendo dentro del shell nativo de verdad. */
export function pushActivo() {
  return IS_APP_BUILD
    && typeof window !== 'undefined'
    && !!window.Capacitor?.isNativePlatform?.();
}

function conPlugin(fn) {
  if (!moduloPromise) moduloPromise = import('@capacitor/push-notifications');
  return moduloPromise.then((mod) => fn(mod.PushNotifications));
}

export function tokenGuardado() {
  if (tokenEnMemoria) return tokenEnMemoria;
  try { tokenEnMemoria = localStorage.getItem(LLAVE_TOKEN) || null; } catch (e) { /* sin almacen */ }
  return tokenEnMemoria;
}

function guardarToken(token) {
  tokenEnMemoria = token;
  try { localStorage.setItem(LLAVE_TOKEN, token); } catch (e) { /* sin almacen */ }
}

function olvidarToken() {
  tokenEnMemoria = null;
  try { localStorage.removeItem(LLAVE_TOKEN); } catch (e) { /* sin almacen */ }
}

function mandarRegistro(token, lang) {
  return Promise.resolve()
    .then(() => cliente().post('/push/register', { token, lang }))
    .catch(() => { /* el siguiente arranque lo vuelve a mandar */ });
}

async function ponerEscuchas(P) {
  if (escuchasPuestos) return;
  escuchasPuestos = true;
  await P.addListener('registration', (t) => {
    const token = t && t.value;
    if (!token) return;
    guardarToken(token);
    mandarRegistro(token, idiomaDeLaApp());
  });
  await P.addListener('registrationError', (e) => {
    // eslint-disable-next-line no-console
    console.warn('[push] registrationError', e && e.error);
  });
  // Con la app al frente no se pinta nada (`presentationOptions: []`) y aqui tampoco se
  // hace nada: el aviso de dentro de la app es el de siempre.
  await P.addListener('pushNotificationReceived', () => {});
  await P.addListener('pushNotificationActionPerformed', (accion) => {
    const datos = accion && accion.notification && accion.notification.data;
    abrirDocumento(datos && datos.doc_id);
  });
}

/** Arranque con sesion y cada inicio de sesion. */
export function arrancarPush() {
  if (!pushActivo()) return Promise.resolve();
  return conPlugin(async (P) => {
    await ponerEscuchas(P);
    const estado = await P.checkPermissions();
    if (estado && estado.receive === 'granted') await P.register();
  }).catch(() => { /* el push jamas cuesta un arranque */ });
}

/** La primera vez que se empieza a procesar un documento desde la app. */
export function pedirPermisoSiToca() {
  if (!pushActivo()) return Promise.resolve();
  return conPlugin(async (P) => {
    await ponerEscuchas(P);
    const estado = await P.checkPermissions();
    if (!estado || estado.receive !== 'prompt') return;
    const respuesta = await P.requestPermissions();
    if (respuesta && respuesta.receive === 'granted') await P.register();
  }).catch(() => { /* ni el dialogo ni su falla pueden frenar una subida */ });
}

/** Al cambiar el idioma: el token vuelve a viajar con el idioma nuevo. */
export function reRegistrarIdioma(lang, user) {
  if (!pushActivo() || !user) return Promise.resolve();
  const token = tokenGuardado();
  if (!token) return Promise.resolve();
  return mandarRegistro(token, idiomaDeLaApp(lang));
}

/** Al cerrar sesion, ANTES de borrar la sesion. Si falla o tarda, se sigue. */
export async function darDeBajaPush() {
  if (!pushActivo()) return;
  const token = tokenGuardado();
  if (!token) return;
  let techo;
  try {
    await Promise.race([
      Promise.resolve().then(() => cliente().post('/push/unregister', { token })),
      new Promise((resolve) => { techo = setTimeout(resolve, BAJA_TECHO_MS); }),
    ]);
  } catch (e) {
    /* la baja jamas convierte un cierre de sesion en error */
  } finally {
    clearTimeout(techo);
    olvidarToken();
  }
}

function tarjeta(docId) {
  const buscada = `doc-${docId}`;
  const todas = Array.from(document.querySelectorAll('[data-testid]'))
    .filter((el) => el.getAttribute('data-testid') === buscada);
  // Una sola (un documento suelto): la de siempre. Dos (una parte, en las dos gemelas de
  // su padre abierta): la que se ve, porque la otra no tiene caja y no se desplaza; si
  // ninguna dice que se ve, la primera.
  if (todas.length < 2) return todas[0] || null;
  return todas.find((el) => el.getClientRects().length > 0) || todas[0];
}

/** Avisa que se busca este documento: la padre de una parte se abre. Jamas frena nada. */
function avisarQueSeBusca(docId) {
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_MOSTRAR_DOC, { detail: { id: docId } }));
  } catch (e) { /* sin evento la busqueda sigue igual */ }
}

/** El toque: al Panel, y la tarjeta a la vista si aparece en 3 s. */
export function abrirDocumento(docId) {
  if (!navegador) {
    pendiente = { docId: docId || null };
    return;
  }
  try { navegador('/dashboard'); } catch (e) { /* sin router no hay a donde ir */ }
  if (!docId) return;
  const inicio = Date.now();
  const buscar = () => {
    avisarQueSeBusca(docId);
    const el = tarjeta(docId);
    if (el) {
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
      return;
    }
    if (Date.now() - inicio >= BUSCAR_TARJETA_MS) return;
    setTimeout(buscar, PASO_MS);
  };
  buscar();
}

/** Lo llama `components/PushPuente.js` con el `navigate` del router. Devuelve su baja. */
export function registrarNavegador(fn) {
  navegador = fn;
  if (pendiente) {
    const { docId } = pendiente;
    pendiente = null;
    abrirDocumento(docId);
  }
  return () => { if (navegador === fn) navegador = null; };
}

/** Solo para las pruebas: el modulo vuelve a su estado de arranque. */
export function __reiniciarPushParaPruebas() {
  moduloPromise = null;
  tokenEnMemoria = null;
  escuchasPuestos = false;
  navegador = null;
  pendiente = null;
}
